import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import type { UserClinicRole } from '../store/slices/authSlice';
import { getAgencyClinicDisplayName } from '../utils/agencyClinicDisplayName';

export const ALL_CLINICS = '__all__';

export interface AccessibleClinic {
  id: string;
  name: string;
  clinic_source: string | null;
  is_kinnser?: boolean;
  clinic_key?: string | null;
  branches?: Array<{ [key: string]: unknown }> | null;
  city?: string | null;
  state?: string | null;
  /** Present when the user has a clinic-scoped role assignment for this clinic. */
  userClinic?: UserClinicRole;
}

export interface UseAccessibleClinicsResult {
  /** Agency clinics available for filtering, resolved to display names and sorted. */
  clinics: AccessibleClinic[];
  /**
   * IDs of clinics the current user may filter to.
   * When the user has clinic-scoped role assignments, this is that set;
   * otherwise it is every loaded agency clinic (backoffice).
   */
  accessibleClinicIds: string[];
  /** Currently selected clinic id, or `ALL_CLINICS` for no filter. */
  selectedClinicId: string;
  setSelectedClinicId: (id: string) => void;
  /** True when no specific clinic is selected. */
  isAllClinics: boolean;
}

/**
 * Agency clinics for list filters (Patient Manager, Hotbox).
 *
 * Temporary EMR-783 bridge (remove when #1935–#1939 land): if the user has any
 * clinic-scoped role assignments (`user.user_clinics`), limit the list to those
 * clinics; if none, treat them as backoffice and show every agency clinic.
 * "All Clinics" filters should intersect with `accessibleClinicIds`.
 */
export function useAccessibleClinics(): UseAccessibleClinicsResult {
  const { user, agencyClinics, fetchAgencyClinics } = useStore(
    useShallow(state => ({
      user: state.user,
      agencyClinics: state.agencyClinics,
      fetchAgencyClinics: state.fetchAgencyClinics,
    }))
  );

  useEffect(() => {
    fetchAgencyClinics(user?.agency?.id);
  }, [fetchAgencyClinics, user?.agency?.id]);

  const clinics = useMemo((): AccessibleClinic[] => {
    const userClinics = user?.user_clinics ?? [];
    const userClinicsById = new Map(userClinics.map(uc => [uc.clinic_id, uc]));
    const restrictToAssignments = userClinics.length > 0;
    const totalClinicCount = agencyClinics.length;
    return agencyClinics
      .map(clinic => ({
        id: clinic.id,
        name: getAgencyClinicDisplayName(clinic, { totalClinicCount }),
        clinic_source: clinic.clinic_source ?? null,
        is_kinnser: clinic.is_kinnser,
        clinic_key: clinic.clinic_key,
        branches: clinic.branches,
        city: clinic.city,
        state: clinic.state,
        userClinic: userClinicsById.get(clinic.id),
      }))
      .filter(clinic => !restrictToAssignments || clinic.userClinic != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [user?.user_clinics, agencyClinics]);

  const accessibleClinicIds = useMemo(
    () => clinics.map(clinic => clinic.id),
    [clinics]
  );

  const [selectedClinicId, setSelectedClinicId] = useState<string>(ALL_CLINICS);

  useEffect(() => {
    if (
      selectedClinicId !== ALL_CLINICS &&
      !clinics.some(c => c.id === selectedClinicId)
    ) {
      setSelectedClinicId(ALL_CLINICS);
    }
  }, [clinics, selectedClinicId]);

  const isAllClinics = selectedClinicId === ALL_CLINICS;

  return {
    clinics,
    accessibleClinicIds,
    selectedClinicId,
    setSelectedClinicId,
    isAllClinics,
  };
}
